/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    makeStyles,
    useMediaQuery,
} from '@material-ui/core';
import { ArrowBackIosRounded, ArrowForwardIosRounded } from '@material-ui/icons';
import {
    arrayOf, shape, string, number, func, object,
    array, bool,
} from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';
import parseFontFamilyExtension from '../../../../../helpers/contentstack/parseFontFamilyExtension';
import noop from '../../../../../helpers/noop';
import ConnectedPersonalizedHomePageProduct from '../PersonalizedHomePageProduct/PersonalizedHomePageProduct';
import { getSalesforceAttributes, getSSRDeviceType } from '../../../../../../state/ducks/App/App-Selectors';
import { getBrandName } from '../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
// import BlockHeading from '../../../../ContentTypeComponents/BlockHeading';
import { getBrandFromPartNumber } from '../../../../../helpers/getBrandFromPartNumber';
import useIsPlaPage from '../../../../../helpers/hooks/useIsPlaPage';
import NavigationPillsContainer from '../../../EnterpriseID/Common/NavigationPillsContainer';
import NavigationPillsContainerV2 from '../../../EnterpriseID/Common/NavigationPillsContainerV2';
import { ProductGridSkeleton } from '../../../GraphqlSkeletonComponents/TabTrendingProductSkeleton';
import { emitCustomTrackingEvent, emitProductImpression } from '../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const useStyles = makeStyles((theme) => ({
    root: (styles) => ({
        padding: styles?.containerPadding || '32px 0px',
        [theme.breakpoints.down(600)]: {
            padding: styles?.containerPadding || '16px 0px',
        },
    }),

    titleContainer: (styles) => ({
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0 10px',

        [theme.breakpoints.up(1024)]: {
            paddingLeft: '20px',
            paddingRight: '20px',
        },

        [theme.breakpoints.up(1400)]: {
            paddingLeft: 0,
            paddingRight: 0,
        },
        '& h2': {
            color: styles?.titleFontColor || '#000000',
            margin: styles?.titleMargin || '0px 18px 16p',
            padding: styles?.padding || '0px',
            font: 'var(--comp-base-text-desktop-header3)',
            fontFamily: 'var(--comp-base-font-family-decorative) !important',
            textAlign: styles?.textAlign || 'center',
            letterSpacing: '0.01em',
            textUnderlinePosition: 'from-font',
            textDecorationSkipInk: 'none',
            [theme.breakpoints.down(600)]: {
                font: 'var(--comp-base-text-mobile-header3)',
            },
        },
        '& #plaTitleTemp': {
            color: styles?.titleFontColor || '#000000',
            margin: styles?.titleMargin || '0px 18px 16p',
            padding: styles?.padding || '0px',
            fontFamily: styles?.titleFontFamily || theme?.typography?.fontFamily,
            textAlign: styles?.textAlign || 'center',
            fontSize: styles?.titleFontSize || '28px',
            fontWeight: styles?.titleFontWeight || '400',
            lineHeight: '35px',
            letterSpacing: '0.01em',
            textUnderlinePosition: 'from-font',
            textDecorationSkipInk: 'none',
            [theme.breakpoints.down(600)]: {
                fontSize: styles?.titleFontSize || '21px',
                lineHeight: '28px',
            },
        },
    }),
    productContainer: {
        margin: '0 auto',
        padding: '0 10px 10px 10px',
        width: '100%',
        height: 'auto',
        maxWidth: '1400px',
        overflow: 'auto',
        whiteSpace: 'nowrap',
        display: 'flex',
        flexWrap: 'nowrap',

        [theme.breakpoints.up(1024)]: {
            paddingLeft: '20px',
            paddingRight: '20px',
        },

        [theme.breakpoints.up(1400)]: {
            paddingLeft: 0,
            paddingRight: 0,
        },
        '&$flexContainer': {
            overflow: 'visible',
        },
        '& [data-component="productName"]': {
            [theme.breakpoints.down(600)]: {
                margin: 0,
                display: '-webkit-box',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                whiteSpace: 'normal',
            },
        },
    },
    carouselRow: {
        display: 'flex',
        alignItems: 'stretch',
        gap: '16px',
        maxWidth: '1464px',
        margin: '0 auto',
        [theme.breakpoints.down(1023)]: {
            display: 'block',
            maxWidth: '1400px',
        },
    },
    viewport: {
        flex: '1 1 auto',
        minWidth: 0,
        overflow: 'hidden',
        [theme.breakpoints.down(1023)]: {
            width: '100%',
            overflow: 'visible',
        },
    },
    productContainerV2: {
        margin: '0 auto',
        padding: '10px',
        width: '100%',
        height: 'auto',
        maxWidth: '1400px',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'smooth',
        whiteSpace: 'nowrap',
        display: 'flex',
        flexWrap: 'nowrap',
        [theme.breakpoints.up(1024)]: {
            paddingLeft: '20px',
            paddingRight: '20px',
        },
        [theme.breakpoints.up(1400)]: {
            paddingLeft: 0,
            paddingRight: 0,
        },
        [theme.breakpoints.up(1024)]: {
            overflow: 'hidden',
        },
        [theme.breakpoints.down(1023)]: {
            padding: '0 16px 10px 16px',
            WebkitOverflowScrolling: 'touch',
            scrollPaddingLeft: '16px',
            scrollPaddingRight: '16px',
        },
    },
    centeredContainer: {
        justifyContent: 'center',
    },
    leftAlignedContainer: {
        justifyContent: 'flex-start',
    },
    alignedContainer: (styles) => ({
        justifyContent: styles?.productAlignment,
    }),
    controlColumn: {
        width: '52px',
        flex: '0 0 52px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'stretch',
        padding: '0 8px',
        boxSizing: 'border-box',
        [theme.breakpoints.down(1023)]: {
            display: 'none',
        },
    },
    emptyArrowPadding: {
        width: '52px',
        flex: '0 0 52px',
        padding: '0 8px',
        boxSizing: 'border-box',
        [theme.breakpoints.down(1023)]: {
            display: 'none',
        },
    },
    controlButton: {
        width: '36px',
        height: '36px',
        borderRadius: '999px',
        border: '1px solid #DDDDDD',
        background: '#FFFFFF',
        color: '#201648',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        boxShadow: 'none',
        outline: 'none',
        appearance: 'none',
        flexShrink: 0,
        '& svg': {
            fontSize: '18px',
        },
    },
    controlButtonDisabled: {
        opacity: 0.45,
        cursor: 'default',
        pointerEvents: 'none',
    },
    flexContainer: ({ isPillsToRender }) => ({
        display: 'flex',
        justifyContent: isPillsToRender ? '' : 'center',
    }),
    plaFlexContainer: (styles) => ({
        display: 'flex',
        justifyContent: styles?.productAlignment,
    }),
    productCard: ({ noOfProductsToShow }) => ({
        width: `calc((100% - ${10 * (noOfProductsToShow - 1)}px) / ${noOfProductsToShow})`,
        marginRight: '10px',
        // display: 'inline-table',
        whiteSpace: 'pre-line',
        flex: '0 0 auto',

        '& a p span': {
            whiteSpace: 'normal',
        },

        '&:last-child': {
            marginRight: '0',
        },

        [theme.breakpoints.up(1024)]: {
            width: `calc((100% - ${20 * (noOfProductsToShow - 1)}px) / ${noOfProductsToShow})`,
            marginRight: '20px',
        },

        [theme.breakpoints.up(1400)]: {
            width: `${(1400 - (20 * (noOfProductsToShow - 1))) / noOfProductsToShow}px`,
        },
    }),
    productCardV2: ({ noOfProductsToShow }) => ({
        width: `calc((100% - ${10 * (noOfProductsToShow - 1)}px) / ${noOfProductsToShow})`,
        marginRight: '10px',
        whiteSpace: 'pre-line',
        flex: '0 0 auto',
        '& a p span': {
            whiteSpace: 'normal',
        },
        '&:last-child': {
            marginRight: '0',
        },
        [theme.breakpoints.up(1024)]: {
            width: '204px',
            marginRight: '20px',
        },
        [theme.breakpoints.down(1023)]: {
            width: '152px',
            minWidth: '152px',
            flexBasis: '152px',
            marginRight: '12px',
        },
    }),
}));

const PersonalizedHomePageCarouselBuilder = ({
    blockData: {
        productsToShow,
        title,
        titleFontColor,
        titleFontSize,
        titleFontStyle,
        titleFontFamily,
        titleFontWeight,
        titleMargin,
        alignTitle,
        padding,
        containerPadding,
        productAlignment,
    },
    products,
    productClickCallback,
    eventTrackingData,
    productStyleConfig,
    sfTitle,
    isFromSFABTestOnPLA,
    navPillsUID,
    isLoading,
    emptyArrowPadding,
    attributionToken,
    dataTestId,
}) => {
    const deviceType = useSelector(getSSRDeviceType);
    const brandName = useSelector(getBrandName);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const dispatch = useDispatch();
    const salesforceAttributes = useSelector(getSalesforceAttributes);
    const scrollRef = useRef(null);
    const isDesktopViewport = useMediaQuery('(min-width: 1024px)');
    const ffIsTrendingUxUpdateEnabled = useSelector(getFeatureFlag('is-trending-ux-update-enabled'));

    const isPlaPage = useIsPlaPage();

    const getProductsToShow = () => {
        const temp = {
            desktop: productsToShow?.desktop || 6,
            mobile: productsToShow?.mobile || 2.5,
            tablet: productsToShow?.tablet || 3.2,
        };

        if (deviceType === 'desktop') {
            return temp.desktop;
        }

        if (deviceType === 'tablet') {
            return temp.tablet;
        }

        return temp.mobile;
    };

    const handleClick = (partNumber) => {
        dispatch(emitCustomTrackingEvent({
            eventType: 'custom',
            eventSubType: 'feature-tracking',
            action: 'Click',
            category: 'Trending Product',
            label: partNumber,
        }));
        productClickCallback(partNumber);
    };

    const noOfProductsToShow = getProductsToShow();
    const keepInCenter = !isFromSFABTestOnPLA && noOfProductsToShow >= (products?.length);
    const productAlignmentSetVal = isFromSFABTestOnPLA ? productAlignment : 'left';

    const styles = {
        titleMargin,
        titleFontColor,
        titleFontSize,
        titleFontStyle,
        titleFontFamily: !titleFontFamily && brandName === 'harryanddavid' ? 'ivypresto-text, Crimson, Garamond, Times, serif' : parseFontFamilyExtension(titleFontFamily),
        titleFontWeight,
        textAlign: alignTitle,
        padding,
        noOfProductsToShow,
        containerPadding,
        productAlignment: productAlignmentSetVal,
        isPillsToRender: Boolean(navPillsUID),
    };

    const classes = useStyles(styles);

    const maxProductsToShow = productStyleConfig?.maximum_number_of_products_to_show_on_carousel || products?.length;
    const visibleProducts = products?.slice(0, maxProductsToShow) || [];
    const showV2Controls = isDesktopViewport && visibleProducts.length > 6;

    const navigationPills = ffIsTrendingUxUpdateEnabled
        ? <NavigationPillsContainerV2 maxWidth="1400px" navPillsUID={navPillsUID} />
        : <NavigationPillsContainer maxWidth="1400px" navPillsUID={navPillsUID} />;

    const getDesktopScrollMetrics = () => {
        const el = scrollRef.current;
        const cards = Array.from(el?.children || []);
        const firstCard = cards[0];

        if (!el || !firstCard || cards.length === 0) {
            return null;
        }

        const computedStyles = window.getComputedStyle(firstCard);
        const marginRight = parseFloat(computedStyles.marginRight) || 0;
        const cardWidth = firstCard.getBoundingClientRect().width + marginRight;
        const containerPaddingLeft = parseFloat(window.getComputedStyle(el).paddingLeft) || 0;
        const cardsPerStep = Math.max(Math.floor(noOfProductsToShow) || 1, 1);
        const maxScrollLeft = Math.max(el.scrollWidth - el.clientWidth, 0);

        if (!cardWidth) {
            return null;
        }

        const getClosestCardIndex = () => cards.reduce((closestIndex, card, index) => {
            const currentDistance = Math.abs(card.offsetLeft - el.scrollLeft);
            const closestDistance = Math.abs(cards[closestIndex].offsetLeft - el.scrollLeft);
            return currentDistance < closestDistance ? index : closestIndex;
        }, 0);

        return {
            el,
            cards,
            cardsPerStep,
            containerPaddingLeft,
            getClosestCardIndex,
            maxScrollLeft,
        };
    };
    const handlePrev = () => {
        const metrics = getDesktopScrollMetrics();

        if (!metrics) {
            return;
        }

        const {
            el,
            cards,
            cardsPerStep,
            containerPaddingLeft,
            getClosestCardIndex,
        } = metrics;
        const currentIndex = getClosestCardIndex();
        const targetIndex = Math.max(currentIndex - cardsPerStep, 0);
        const targetScrollLeft = targetIndex === 0
            ? 0
            : Math.max((cards[targetIndex]?.offsetLeft || 0) - containerPaddingLeft, 0);

        el.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    };

    const handleNext = () => {
        const metrics = getDesktopScrollMetrics();

        if (!metrics) {
            return;
        }

        const {
            el,
            cards,
            cardsPerStep,
            containerPaddingLeft,
            getClosestCardIndex,
            maxScrollLeft,
        } = metrics;
        const currentIndex = getClosestCardIndex();
        const targetIndex = Math.min(currentIndex + cardsPerStep, cards.length - 1);
        const targetScrollLeft = Math.min(
            Math.max((cards[targetIndex]?.offsetLeft || 0) - containerPaddingLeft, 0),
            maxScrollLeft,
        );

        el.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    };

    const legacyCarousel = (
        <div className={`${classes.productContainer} ${keepInCenter ? classes.flexContainer : classes.plaFlexContainer}`}>
            {products?.slice(0, maxProductsToShow).map((product) => {
                const {
                    name, image, seo: { url }, skuPriceRange, availability, partNumber, isPassportEligible, productDeliveryType,
                } = product;
                const link = url || '';
                const productBrandId = getBrandFromPartNumber(partNumber);

                return (
                    <div key={partNumber} className={classes.productCard}>
                        <ConnectedPersonalizedHomePageProduct
                            name={name}
                            url={link}
                            image={image}
                            skuPriceRange={skuPriceRange}
                            availability={availability}
                            partNumber={partNumber}
                            showSimpleProductRedesignAbTest={false}
                            productBrand={productBrandId}
                            productClickCallback={() => handleClick(partNumber)}
                            productSkus={product.productSkus}
                            eventTrackingData={eventTrackingData}
                            productStyleConfig={productStyleConfig}
                            priceRangeLayout={productStyleConfig?.price_range_layout || []}
                            isPassportEligible={isPassportEligible}
                            productDeliveryType={productDeliveryType}
                            attributionToken={attributionToken}
                        />
                    </div>
                );
            })}
        </div>
    );

    const v2Carousel = (
        <div className={classes.carouselRow}>
            {showV2Controls && (
                <div className={classes.controlColumn}>
                    <button
                        type="button"
                        className={`${classes.controlButton} ${!canScrollPrev ? classes.controlButtonDisabled : ''}`}
                        onClick={handlePrev}
                        disabled={!canScrollPrev}
                        aria-label="Scroll products left"
                    >
                        <ArrowBackIosRounded />
                    </button>
                </div>
            )}
            {emptyArrowPadding && !showV2Controls && <div className={classes.emptyArrowPadding} />}
            <div className={classes.viewport}>
                <div
                    ref={scrollRef}
                    className={`${classes.productContainerV2} ${visibleProducts.length === 6 ? classes.centeredContainer : classes.leftAlignedContainer}`}
                >
                    {visibleProducts.map((product) => {
                        const {
                            name, image, seo: { url }, skuPriceRange, availability, partNumber, isPassportEligible, productDeliveryType,
                        } = product;
                        const link = url || '';
                        const productBrandId = getBrandFromPartNumber(partNumber);

                        return (
                            <div key={partNumber} className={classes.productCardV2}>
                                <ConnectedPersonalizedHomePageProduct
                                    name={name}
                                    url={link}
                                    image={image}
                                    skuPriceRange={skuPriceRange}
                                    availability={availability}
                                    partNumber={partNumber}
                                    showSimpleProductRedesignAbTest={false}
                                    productBrand={productBrandId}
                                    productClickCallback={() => handleClick(partNumber)}
                                    productSkus={product.productSkus}
                                    eventTrackingData={eventTrackingData}
                                    productStyleConfig={productStyleConfig}
                                    priceRangeLayout={productStyleConfig?.price_range_layout || []}
                                    isPassportEligible={isPassportEligible}
                                    productDeliveryType={productDeliveryType}
                                    attributionToken={attributionToken}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
            {emptyArrowPadding && !showV2Controls && <div className={classes.emptyArrowPadding} />}
            {showV2Controls && (
                <div className={classes.controlColumn}>
                    <button
                        type="button"
                        className={`${classes.controlButton} ${!canScrollNext ? classes.controlButtonDisabled : ''}`}
                        onClick={handleNext}
                        disabled={!canScrollNext}
                        aria-label="Scroll products right"
                    >
                        <ArrowForwardIosRounded />
                    </button>
                </div>
            )}
        </div>
    );

    useEffect(() => {
        if (!ffIsTrendingUxUpdateEnabled || !showV2Controls) {
            setCanScrollPrev(false);
            setCanScrollNext(false);
            return undefined;
        }

        const updateScrollButtons = () => {
            const el = scrollRef.current;
            if (!el || !isDesktopViewport) {
                setCanScrollPrev(false);
                setCanScrollNext(false);
                return;
            }

            const maxScrollLeft = Math.max(el.scrollWidth - el.clientWidth, 0);
            setCanScrollPrev(el.scrollLeft > 0);
            setCanScrollNext(maxScrollLeft > 1 && el.scrollLeft < maxScrollLeft - 1);
        };

        updateScrollButtons();
        const currentRef = scrollRef.current;
        currentRef?.addEventListener('scroll', updateScrollButtons);
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', updateScrollButtons);
        }

        return () => {
            currentRef?.removeEventListener('scroll', updateScrollButtons);
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', updateScrollButtons);
            }
        };
    }, [products, isDesktopViewport, noOfProductsToShow, ffIsTrendingUxUpdateEnabled, showV2Controls]);

    const carouselContent = ffIsTrendingUxUpdateEnabled ? v2Carousel : legacyCarousel;
    const bodyContent = isLoading
        ? <ProductGridSkeleton showCircularSkeleton={false} numOfProducts={6} />
        : carouselContent;

    if (products?.length > 0) {
        return (
            <div className={classes.root} data-testid={dataTestId}>
                <div className={classes.titleContainer}>
                    {isPlaPage
                        ? <div id="plaTitleTemp">{sfTitle || title}</div>
                        : <h2>{sfTitle || title}</h2>}
                </div>
                {navigationPills}
                {bodyContent}
            </div>
        );
    }

    return null;
};

PersonalizedHomePageCarouselBuilder.propTypes = {
    products: arrayOf(shape({
        id: string.isRequired,
        image: shape({
            path: string.isRequired,
            name: string.isRequired,
        }).isRequired,
        name: string.isRequired,
        skuPriceRange: shape({
            retail: arrayOf(shape({
                value: number.isRequired,
            })).isRequired,
            sale: arrayOf(shape({
                value: number.isRequired,
            })).isRequired,
        }).isRequired,
        seo: shape({
            url: string.isRequired,
        }).isRequired,
    })).isRequired,
    blockData: shape({
        title: string,
        titleFontColor: string,
        titleFontSize: string,
        titleFontStyle: string,
        titleFontFamily: string,
        titleFontWeight: string,
        titleMargin: string,
        padding: string,
        align_title: string,
        productsToShow: shape({
            desktop: number,
            mobile: number,
            tablet: number,
        }),
    }),
    title: string,
    productClickCallback: func,
    eventTrackingData: object,
    productStyleConfig: array,
    sfTitle: string,
    isFromSFABTestOnPLA: bool,
    navPillsUID: object,
    isLoading: bool,
    attributionToken: string,
    dataTestId: string,
    emptyArrowPadding: bool,
};

PersonalizedHomePageCarouselBuilder.defaultProps = {
    blockData: {
        title: '',
        titleFontColor: '',
        titleFontSize: '',
        titleFontStyle: '',
        titleFontFamily: '',
        titleFontWeight: '',
        titleMargin: '',
        padding: '',
        align_title: '',
        productsToShow: null,
    },
    title: '',
    productClickCallback: noop,
    eventTrackingData: {},
    productStyleConfig: [],
    sfTitle: '',
    isFromSFABTestOnPLA: false,
    navPillsUID: null,
    isLoading: false,
    emptyArrowPadding: false,
    attributionToken: '',
    dataTestId: 'carousel-builder',
};

export default PersonalizedHomePageCarouselBuilder;
